/**
 * @file     focusTarget.ts
 * @purpose  "Brings focus": a CTA that starts an action elsewhere on the page scrolls the target into view and moves keyboard focus into its first field. Also installs the one document-level handler that gives every same-page hash link that behaviour, and the first-field lookup sheets and modals use on open.
 */

// tabindex="-1" fields are honeypots and other inputs kept out of the tab order on purpose.
const FIELD_SELECTOR = [
  'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
].join(', ');

const FOCUSABLE_SELECTOR = [
  FIELD_SELECTOR,
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export type FocusTargetMode = 'field' | 'target';

export type FocusTargetOptions = {
  /** `field` (default) focuses the first field inside the target (else its first control); `target` focuses the target itself, for section links. */
  mode?: FocusTargetMode;
  block?: ScrollLogicalPosition;
};

function isRendered(el: HTMLElement): boolean {
  if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  if (typeof el.checkVisibility === 'function') return el.checkVisibility({ visibilityProperty: true });
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

/** First visible, enabled form field inside `root`, or null. */
export function firstField(root: ParentNode): HTMLElement | null {
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(FIELD_SELECTOR))) {
    if (isRendered(el)) return el;
  }
  return null;
}

/** First visible focusable element inside `root`, or null. */
export function firstFocusable(root: ParentNode): HTMLElement | null {
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))) {
    if (isRendered(el)) return el;
  }
  return null;
}

export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function resolve(target: string | Element | null): HTMLElement | null {
  if (target == null) return null;
  if (typeof target !== 'string') return target as HTMLElement;
  const id = target.startsWith('#') ? target.slice(1) : target;
  if (!id) return null;
  return document.getElementById(id);
}

/**
 * Scrolls `target` into view and focuses its first field, else its first
 * control, else the target itself (always the target in `target` mode). Focus moves with
 * preventScroll so the one scroll is the smooth one, which reduced motion
 * turns into a jump. Returns false when the target is not on the page.
 */
export function focusTarget(target: string | Element | null, options: FocusTargetOptions = {}): boolean {
  const el = resolve(target);
  if (!el) return false;
  const { mode = 'field', block = 'start' } = options;
  const focusEl = (mode === 'field' ? firstField(el) ?? firstFocusable(el) : null) ?? el;
  if (focusEl === el && !el.hasAttribute('tabindex') && !el.matches(FOCUSABLE_SELECTOR)) {
    el.setAttribute('tabindex', '-1');
  }
  focusEl.focus({ preventScroll: true });
  el.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block });
  return true;
}

function samePageHash(anchor: HTMLAnchorElement): string | null {
  const raw = anchor.getAttribute('href');
  if (!raw || !raw.includes('#')) return null;
  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  if (url.pathname !== window.location.pathname || url.search !== window.location.search) return null;
  return url.hash.length > 1 ? url.hash : null;
}

/**
 * Document-level click handler for same-page hash links. Runs in the capture
 * phase so it sees the click before a framework link would treat it as a
 * navigation. Links opt into section behaviour with `data-focus="target"`
 * (table-of-contents style), or out entirely with `data-focus="off"`.
 */
export function handleInPageLinkClick(event: MouseEvent): void {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
  if (anchor.dataset.focus === 'off') return;
  const hash = samePageHash(anchor);
  if (!hash || !resolve(hash)) return;
  event.preventDefault();
  if (window.location.hash !== hash) {
    const oldURL = window.location.href;
    window.history.pushState(window.history.state, '', hash);
    // pushState is silent; listeners that track the hash still need to hear it.
    window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL, newURL: window.location.href }));
  }
  focusTarget(hash, { mode: anchor.dataset.focus === 'target' ? 'target' : 'field' });
}

/** Installs the hash-link handler once; returns its uninstaller. */
export function installInPageFocusLinks(): () => void {
  document.addEventListener('click', handleInPageLinkClick, true);
  return () => document.removeEventListener('click', handleInPageLinkClick, true);
}
